/**
 * PATH: packages/ui/src/hooks/useTabKeyboardShortcuts.ts
 * PURPOSE: Keyboard shortcuts for browser-style tab navigation.
 *
 * SHORTCUTS:
 *  - Ctrl/Cmd + T       → New tab
 *  - Ctrl/Cmd + W       → Close active tab
 *  - Ctrl/Cmd + Tab     → Next tab
 *  - Ctrl/Cmd + Shift+Tab → Previous tab
 *  - Ctrl/Cmd + 1-9     → Jump to tab by position
 *
 * USAGE:
 *   useTabKeyboardShortcuts({ useStore, ...tabNav })
 *
 * DEPENDENCIES:
 *  - Tab store created via createTabStore
 *  - TabNavigationReturn handlers
 */

import { useEffect } from 'react'
import type { TabStore } from '../stores/createTabStore'

interface TabShortcutConfig {
  /** Zustand tab store hook (for reading current tab state) */
  useStore: { getState: () => TabStore }
  /** Handle tab click (navigate) */
  handleTabClick: (tabId: string) => void
  /** Handle tab close */
  handleTabClose: (tabId: string) => void
  /** Handle new tab */
  handleNewTab: () => void
}

export function useTabKeyboardShortcuts({
  useStore,
  handleTabClick,
  handleTabClose,
  handleNewTab,
}: TabShortcutConfig) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isModifier = e.metaKey || e.ctrlKey
      if (!isModifier) return

      // Don't intercept when typing in inputs
      const target = e.target as HTMLElement
      const isTyping = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable
      if (isTyping) return

      const { tabs, activeTabId } = useStore.getState()

      // Ctrl+T → New Tab
      if (e.key === 't' && !e.shiftKey) {
        e.preventDefault()
        handleNewTab()
        return
      }

      // Ctrl+W → Close Active Tab
      if (e.key === 'w' && !e.shiftKey) {
        e.preventDefault()
        if (activeTabId && tabs.length > 1) {
          const activeTab = tabs.find(t => t.id === activeTabId)
          if (activeTab && !activeTab.isPinned) {
            handleTabClose(activeTabId)
          }
        }
        return
      }

      // Ctrl+Tab → Next Tab / Ctrl+Shift+Tab → Previous Tab
      if (e.key === 'Tab') {
        e.preventDefault()
        const currentIndex = tabs.findIndex(t => t.id === activeTabId)
        if (currentIndex === -1) return

        let nextIndex: number
        if (e.shiftKey) {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1
        } else {
          nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0
        }

        handleTabClick(tabs[nextIndex].id)
        return
      }

      // Ctrl+1 through Ctrl+9 → Jump to tab by position
      const digit = parseInt(e.key, 10)
      if (digit >= 1 && digit <= 9) {
        e.preventDefault()
        const targetIndex = digit === 9 ? tabs.length - 1 : digit - 1
        if (targetIndex < tabs.length) {
          handleTabClick(tabs[targetIndex].id)
        }
        return
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [useStore, handleTabClick, handleTabClose, handleNewTab])
}

export default useTabKeyboardShortcuts
